<script setup lang="ts">
/**
 * 상품군 구매 `/products/:groupCode` (079) — 고정 구성(면접 로드맵 등)을 다중 선택해 한 주문으로 구매한다.
 *
 * - 구성·단품가·범위는 서버 카탈로그, 최종 금액·할인액은 서버 견적(POST /orders/quotes)만 쓴다.
 * - 선택 상태는 URL `?options=PART_2,PART_3`(구성 code)와 양방향 동기화 — 새로고침·로그인 복귀에도 보존된다.
 * - 보유·확정 대기 구성은 선택 불가(ownership API). 로그인 전에는 견적·보유 조회를 하지 않는다.
 * - 수량형(합격컷) 상품군은 대학 선택 화면이므로 서버가 준 구매 경로로 보낸다.
 * - 결제는 /checkout?group=…&options=… 로 넘기며 두 상품군 선택을 한 cart 로 합치지 않는다.
 * - 발췌본이 있는 상품군은 구성 선택 바로 위에서 내용을 확인시킨다(#341 — 구매 전 상품 확인, 비로그인 허용).
 * - 이 상품군의 무료 이벤트가 열려 있으면 상단에 안내 띠를 둔다(081) — 유료 구성·견적 흐름은 그대로다.
 */
import { useQuery } from '@tanstack/vue-query';
import { useHead } from '@unhead/vue';
import { FileText } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { fetchQuote } from '#/api/order';
import ProductOptionPicker from '#/components/fo/product/ProductOptionPicker.vue';
import ProductPreviewModal from '#/components/fo/product/ProductPreviewModal.vue';
import ProductQuotePanel from '#/components/fo/product/ProductQuotePanel.vue';
import InterviewEventBanner from '#/components/fo/event/interview/InterviewEventBanner.vue';
import { BaseButton, EmptyState, PageShell, PageTitle } from '#/components/ui';
import { isHttpStatus } from '#/api/http';
import { useCatalogGroup, useCatalogOwnership } from '#/composables/useCatalog';
import { usePageNotFound } from '#/composables/usePageNotFound';
import { buildDocumentTitle } from '#/composables/useRouteHead';
import { findProductPreview } from '#/data';
import { won } from '#/data/pricing';
import { INTERVIEW_EVENT_GROUP } from '#/config/interviewEvent';
import { useAuthStore } from '#/stores/auth';
import { catalogGroupDescription, jsonLdScript, productLd } from '#/lib/structuredData';
import { useCommerceStore } from '#/stores/commerce';

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const commerce = useCommerceStore();

/** 면접 무료 이벤트 띠(081) — 이 화면이 면접 상품군이고 이벤트가 열려 있을 때만. */
const showInterviewEvent = computed(
  () =>
    groupCode.value === INTERVIEW_EVENT_GROUP && commerce.isFreeEventOpen(INTERVIEW_EVENT_GROUP),
);

const groupCode = computed(() => String(route.params.groupCode ?? ''));
const { data: group, isPending, isError, error, refetch } = useCatalogGroup(groupCode);

/** 없는(또는 판매 게시 해제된) 상품군 — 서버 404 만 데이터 없음으로 본다(일시 오류는 noindex 대상 아님). */
usePageNotFound(() => isHttpStatus(error.value, 404));

/**
 * 문서 제목·설명·Product 구조화 데이터 — 상품군 데이터 기준(라우트 meta.title '상품 구매' 는 데이터 도착 전 대체값).
 * 프리렌더는 빌드 시 카탈로그로 채워지므로(src/ssg/prefetch.ts) 크롤러가 상품명·가격을 HTML 에서 바로 읽는다.
 */
useHead({
  title: () => (group.value ? buildDocumentTitle(group.value.name) : undefined),
  meta: () =>
    group.value ? [{ name: 'description', content: catalogGroupDescription(group.value) }] : [],
  script: () => {
    const ld = group.value ? productLd(group.value, `/products/${group.value.code}`) : null;
    return ld ? [jsonLdScript(ld)] : [];
  },
});

const hasPreview = computed(() => findProductPreview(groupCode.value) !== null);
const previewOpen = ref(false);

const isVerified = computed(() => auth.authState === 'verified');
const { data: ownership } = useCatalogOwnership(groupCode, isVerified);

const ownedIds = computed(() => ownership.value?.ownedOptionIds ?? []);
const pendingIds = computed(() => ownership.value?.pendingOptionIds ?? []);

const options = computed(() =>
  [...(group.value?.options ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
);

/** 선택한 구성 id — 구성 code 쿼리로 프리셀렉트하고, 선택 변경은 쿼리로 되돌려 쓴다. */
const selectedIds = ref<number[]>([]);

function unselectable(id: number): boolean {
  return ownedIds.value.includes(id) || pendingIds.value.includes(id);
}

/** URL 쿼리 → 선택(구성 로드·보유 현황 도착 시 재적용). 선택 불가 구성은 걸러낸다. */
function applyQueryPreselect(): void {
  const raw = route.query.options;
  const codes = (Array.isArray(raw) ? raw.join(',') : (raw ?? '')).split(',').filter(Boolean);
  const max = group.value?.maxSelectableCount ?? Number.POSITIVE_INFINITY;
  selectedIds.value = options.value
    .filter((o) => codes.includes(o.code) && !unselectable(o.id))
    .map((o) => o.id)
    .slice(0, max);
}
watch([options, ownedIds, pendingIds], applyQueryPreselect, { immediate: true });

/** 한 주문 최대 선택 수(서버 maxSelectableCount) — 묶음 가격표가 있으면 표 길이 이하. */
const maxSelectable = computed(() => group.value?.maxSelectableCount);

function onToggle(id: number): void {
  if (unselectable(id)) return;
  const adding = !selectedIds.value.includes(id);
  if (
    adding &&
    maxSelectable.value !== undefined &&
    selectedIds.value.length >= maxSelectable.value
  )
    return;
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter((x) => x !== id)
    : [...selectedIds.value, id];
  const codes = options.value.filter((o) => selectedIds.value.includes(o.id)).map((o) => o.code);
  void router.replace({
    query: { ...route.query, options: codes.length ? codes.join(',') : undefined },
  });
}

/** 서버 견적 — 로그인(인증 완료) + 선택 1개 이상일 때만 조회. 같은 선택은 캐시하지 않는다. */
const sortedSelection = computed(() => [...selectedIds.value].sort((a, b) => a - b));
const {
  data: quote,
  isFetching: quoteLoading,
  isError: quoteError,
  refetch: refetchQuote,
} = useQuery({
  queryKey: computed(() => ['catalog', 'quote', groupCode.value, sortedSelection.value]),
  queryFn: () => fetchQuote({ groupCode: groupCode.value, optionIds: sortedSelection.value }),
  enabled: computed(() => isVerified.value && sortedSelection.value.length > 0),
  staleTime: 0,
  gcTime: 0,
  retry: false,
});

const panelState = computed(() => {
  if (selectedIds.value.length === 0) return 'idle';
  if (!isVerified.value) return 'guest';
  if (quoteLoading.value) return 'loading';
  if (quoteError.value) return 'error';
  return quote.value ? 'ready' : 'loading';
});

const submitLabel = computed(() => (isVerified.value ? '구매하기' : '로그인하고 구매하기'));

function onSubmit(): void {
  if (selectedIds.value.length === 0) return;
  if (!isVerified.value) {
    // 선택(쿼리)을 보존한 현재 주소로 복귀시킨다.
    void router.push({
      name: 'error-auth-required',
      query: { redirect: route.fullPath, reason: auth.authState === 'guest' ? 'login' : 'verify' },
    });
    return;
  }
  void router.push({
    name: 'checkout',
    query: { group: groupCode.value, options: sortedSelection.value.join(',') },
  });
}

// 수량형 상품군은 대학 선택 화면으로 — 서버가 준 경로를 그대로 따른다.
watch(group, (g) => {
  if (g && g.pricingType === 'QUANTITY_TIER') void router.replace(g.purchaseAction.path);
});
</script>

<template>
  <PageShell variant="list">
    <PageTitle
      eyebrow="상품"
      :title="group?.name ?? '상품 구매'"
      :description="group?.summary ?? '필요한 구성을 골라 한 번에 구매하세요.'"
    />

    <InterviewEventBanner v-if="showInterviewEvent" variant="inline" />

    <div v-if="isPending" class="py-20 text-center text-[13.5px] text-muted" role="status">
      상품 정보를 불러오는 중입니다…
    </div>

    <EmptyState
      v-else-if="isError || !group"
      title="상품을 찾을 수 없습니다"
      description="판매가 종료되었거나 잘못된 주소입니다."
    >
      <template #action>
        <BaseButton variant="line" size="md" @click="() => refetch()">다시 시도</BaseButton>
      </template>
    </EmptyState>

    <EmptyState
      v-else-if="options.length === 0"
      title="구성을 준비 중입니다"
      description="곧 만나보실 수 있어요."
    />

    <div v-else class="grid grid-cols-1 items-start gap-8 lg:grid-cols-[1fr_360px]">
      <div class="min-w-0">
        <p class="mb-4 text-[13.5px] leading-[1.6] text-body">{{ group.purpose }}</p>
        <div
          v-if="hasPreview"
          class="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-card border border-line bg-bg-2 px-4 py-3"
        >
          <p class="m-0 flex items-center gap-2 text-[13.5px] font-semibold text-brand">
            <FileText class="h-4 w-4 flex-none text-accent-deep" :stroke-width="1.9" />
            구매 전에 실제 지면을 미리 확인하세요
          </p>
          <BaseButton variant="line" size="sm" @click="previewOpen = true"
            >내용 미리보기</BaseButton
          >
        </div>
        <div
          v-if="group.bundlePricing"
          class="mb-5 rounded-card border border-line bg-bg-2 px-4 py-3"
          aria-label="묶음 가격"
        >
          <p class="m-0 flex flex-wrap gap-x-4 gap-y-1 text-[13.5px] font-semibold text-brand">
            <span v-for="t in group.bundlePricing.tiers" :key="t.count">
              {{ t.count }}개 <b class="font-extrabold">{{ won(t.amount) }}원</b>
            </span>
          </p>
          <p class="m-0 mt-1 text-[12.5px] leading-[1.5] text-muted">
            {{ group.bundlePricing.notice }}
          </p>
        </div>
        <ProductOptionPicker
          :options="options"
          :selected-ids="selectedIds"
          :owned-ids="ownedIds"
          :pending-ids="pendingIds"
          :max-selectable="maxSelectable"
          @toggle="onToggle"
        />
      </div>
      <aside class="lg:sticky lg:top-sticky-aside">
        <ProductQuotePanel
          :quote="quote"
          :selected-count="selectedIds.length"
          :state="panelState"
          :cap-notice="group.bundlePricing?.notice ?? group.orderCap?.notice ?? null"
          :submit-label="submitLabel"
          @submit="onSubmit"
          @retry="() => refetchQuote()"
        />
      </aside>
    </div>

    <ProductPreviewModal
      v-if="hasPreview"
      v-model:open="previewOpen"
      :group-code="groupCode"
      placement="product"
    />
  </PageShell>
</template>
